/**
 * Shared helpers for station delivery "legs" (stops) — the frontend mirror of
 * the backend's libs/logistics/deliveryLegs.js.
 *
 * A delivery has 1-4 legs stored as wide fields: primary* / secondary* /
 * tertiary* / quaternary* (stationId, tankNumber, deliveryAmount,
 * productType), plus one loading number per terminal per leg (leg 1 is the
 * terminal's base LoadingNumber field, legs 2-4 are the
 * Secondary/Tertiary/Quaternary variants).
 */

export const LEG_PREFIXES = ['primary', 'secondary', 'tertiary', 'quaternary'];
export const MAX_LEGS = LEG_PREFIXES.length;
export const TERMINAL_PREFIXES = ['firstTerminal', 'secondTerminal'];

const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const legPrefix = (legNumber) => LEG_PREFIXES[legNumber - 1];

/**
 * Field name for a per-leg value, e.g. legField(3, 'stationId') => 'tertiaryStationId'
 */
export const legField = (legNumber, field) => `${legPrefix(legNumber)}${upperFirst(field)}`;

/**
 * Field name for a terminal's loading number for a leg.
 * loadingNumberField('firstTerminal', 1) => 'firstTerminalLoadingNumber'
 * loadingNumberField('firstTerminal', 3) => 'firstTerminalTertiaryLoadingNumber'
 */
export const loadingNumberField = (terminalPrefix, legNumber) => {
	if (legNumber === 1) {
		return `${terminalPrefix}LoadingNumber`;
	}
	return `${terminalPrefix}${upperFirst(legPrefix(legNumber))}LoadingNumber`;
};

/**
 * Field name for a terminal side's per-leg pickup amount (computed by the
 * backend's fetch), e.g. fuelToPickupField('firstTerminal', 2) =>
 * 'firstTerminalFuelToPickupForSecondaryStation'
 */
export const fuelToPickupField = (terminalPrefix, legNumber) =>
	`${terminalPrefix}FuelToPickupFor${upperFirst(legPrefix(legNumber))}Station`;

/**
 * Build the ordered legs array from a delivery (or any object carrying the
 * wide fields). A leg exists when its stationId is set; legs are contiguous,
 * so reading stops at the first gap.
 */
export const getLegs = (delivery) => {
	const legs = [];
	for (let legNumber = 1; legNumber <= MAX_LEGS; legNumber++) {
		const stationId = delivery[legField(legNumber, 'stationId')];
		if (!stationId) {
			break;
		}
		const leg = {
			legNumber,
			stationId,
			tankNumber: delivery[legField(legNumber, 'tankNumber')],
			deliveryAmount: delivery[legField(legNumber, 'deliveryAmount')],
			productType: delivery[legField(legNumber, 'productType')],
			loadingNumbers: {},
		};
		TERMINAL_PREFIXES.forEach((terminalPrefix) => {
			leg.loadingNumbers[terminalPrefix] = delivery[loadingNumberField(terminalPrefix, legNumber)];
		});
		legs.push(leg);
	}
	return legs;
};

/**
 * - legCount: number of legs
 * - distinctStationCount: unique stations across legs
 * - isMultiLeg (old isTwo): more than one leg, including two tanks at one station
 * - isSplit: more than one distinct station
 */
export const getFlags = (legsOrDelivery) => {
	const legs = Array.isArray(legsOrDelivery) ? legsOrDelivery : getLegs(legsOrDelivery);
	const legCount = legs.length;
	const distinctStationCount = new Set(legs.map((leg) => leg.stationId)).size;
	return {
		legCount,
		distinctStationCount,
		isMultiLeg: legCount >= 2,
		isSplit: distinctStationCount >= 2,
	};
};
